import React from 'react';
import { cn } from '@/lib/utils';

interface TimelineItemProps {
  children: React.ReactNode;
  className?: string;
}

interface TimelinePointProps {
  icon?: React.ReactNode;
  className?: string;
}

interface TimelineContentProps {
  children: React.ReactNode;
  className?: string;
}

interface TimelineTimeProps {
  children: React.ReactNode;
  className?: string;
}

interface TimelineTitleProps {
  children: React.ReactNode;
  className?: string;
}

interface TimelineBodyProps {
  children: React.ReactNode;
  className?: string;
}

export const Timeline = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <ol className={cn('relative border-l border-blue-600/30 dark:border-blue-600/30', className)}>
      {children}
    </ol>
  );
};

export const TimelineItem = ({ children, className }: TimelineItemProps) => {
  return (
    <li className={cn('mb-10 ml-6', className)}>
      {children}
    </li>
  );
};

export const TimelinePoint = ({ icon, className }: TimelinePointProps) => {
  return (
    <span
      className={cn(
        'absolute flex items-center justify-center w-6 h-6 bg-blue-600 rounded-full -left-3 ring-8 ring-black',
        className
      )}
    >
      {icon || (
        <svg
          className="w-3 h-3 text-white"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z" />
        </svg>
      )}
    </span>
  );
};

export const TimelineContent = ({ children, className }: TimelineContentProps) => {
  return <div className={cn('space-y-2', className)}>{children}</div>;
};

export const TimelineTime = ({ children, className }: TimelineTimeProps) => {
  return (
    <time className={cn('text-sm font-normal leading-none text-slate-400', className)}>
      {children}
    </time>
  );
};

export const TimelineTitle = ({ children, className }: TimelineTitleProps) => {
  return (
    <h3 className={cn('text-lg font-semibold text-slate-50', className)}>{children}</h3>
  );
};

export const TimelineBody = ({ children, className }: TimelineBodyProps) => {
  return (
    <p className={cn('text-base font-normal text-slate-300 leading-relaxed', className)}>
      {children}
    </p>
  );
};

